import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    PlusOutlined,
    DeleteOutlined,
    CloseOutlined,
    LinkOutlined,
    SettingOutlined,
    HolderOutlined,
    LockOutlined
} from '@ant-design/icons';
import {
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Drawer,
    Popconfirm,
    Popover,
    Progress,
    Result,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { ReactSortable } from 'react-sortablejs';

import { CreateTaskModal } from './CreateTask';
import { FILTER_FIELDS, useExportTasks } from './hooks';
import './ImportPage.scss';
import {
    ExportTask,
    ExportTaskCreateRequest,
    ExportTasksFilterRequest
} from './types';
import { ROLES } from '../../helpers/utils/auth';
import { showErrorMessage, showSuccessMessage } from '../../helpers/utils/ui';
import MainLayout from '../../shared/common/components/layouts/MainLayout';
import usePermission from '../../shared/common/hooks/usePermission';
import { useTypedSelector } from '../../shared/common/hooks/useTypedSelector';

const { Title, Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
    'Создана': 'blue',
    'Выполнена': 'green',
    'Ошибка': 'red',
    'В_работе': 'orange',
};

const PROGRESS_TEXT_MAP: Record<string, string> = {
    'Процесс завершен успешно': 'Импорт завершён успешно',
};

const splitProgressText = (input: string) => {
    if (!input) return { percent: null, text: '-' };
    const match = input.match(/^([\d.]+)%\s*-\s*(.+)$/);
    if (match) {
        const rawText = match[2];
        return {
            percent: parseFloat(match[1]),
            text: PROGRESS_TEXT_MAP[rawText] || rawText,
        };
    }
    return {
        percent: null,
        text: PROGRESS_TEXT_MAP[input] || input,
    };
};

const HELP_LINK_IMPORT = 'https://doc.scananalytics.ru/dannye/import/';

const STORAGE_KEY = 'import_column_settings';

interface ColumnMeta {
    id: string;
    key: string;
    title: string;
}

const DEFAULT_COLUMNS: ColumnMeta[] = [
    { id: 'task_id', key: 'task_id', title: 'ID' },
    { id: 'created_at', key: 'created_at', title: 'Дата создания' },
    { id: 'source_export_created_at', key: 'source_export_created_at', title: 'Дата создания в GC' },
    { id: 'completed_at', key: 'completed_at', title: 'Дата завершения' },
    { id: 'source_name', key: 'source_name', title: 'Источник' },
    { id: 'source_title', key: 'source_title', title: 'Школа / Аккаунт РК' },
    { id: 'creation_type_name', key: 'creation_type_name', title: 'Тип создания' },
    { id: 'task_type_name', key: 'task_type_name', title: 'Тип задачи' },
    { id: 'period', key: 'period', title: 'Период данных' },
    { id: 'external_export_id', key: 'external_export_id', title: 'ID выгрузки GC' },
    { id: 'status_name', key: 'status_name', title: 'Статус' },
    { id: 'progress_percentage', key: 'progress_percentage', title: 'Прогресс' },
    { id: 'created_by_user_name', key: 'created_by_user_name', title: 'Создатель' },
    { id: 'error_message', key: 'error_message', title: 'Ошибка' },
    { id: 'actions', key: 'actions', title: 'Действия' },
];

const DEFAULT_ORDER = DEFAULT_COLUMNS.map(c => c.key);
const DEFAULT_HIDDEN = ['task_id'];

function loadColumnSettings(): { order: string[]; hidden: string[] } {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed.order) && Array.isArray(parsed.hidden)) {
                return parsed;
            }
        }
    } catch {
        // ignore
    }
    return { order: DEFAULT_ORDER, hidden: [...DEFAULT_HIDDEN] };
}

function saveColumnSettings(order: string[], hidden: string[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ order, hidden }));
}

const ImportPage: React.FC = () => {
    const permission = usePermission();
    const selectedSchool = useTypedSelector((state) => state.currentSchool.selectedSchool);
    const [ showFilters, setShowFilters ] = useState(false);
    const [ isModalVisible, setModalVisible ] = useState(false);
    const [ helpDrawerVisible, setHelpDrawerVisible ] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Настройки колонок
    const initialSettings = useMemo(() => loadColumnSettings(), []);
    const [columnOrder, setColumnOrder] = useState<string[]>(initialSettings.order);
    const [hiddenColumns, setHiddenColumns] = useState<string[]>(initialSettings.hidden);
    const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);

    // Элементы для ReactSortable (нужен id)
    const sortableItems = useMemo(() => {
        return columnOrder.map(key => {
            const meta = DEFAULT_COLUMNS.find(c => c.key === key);
            return meta || { id: key, key, title: key };
        });
    }, [columnOrder]);

    const handleSortableChange = (newList: ColumnMeta[]) => {
        const newOrder = newList.map(item => item.key);
        setColumnOrder(newOrder);
        saveColumnSettings(newOrder, hiddenColumns);
    };

    const handleToggleColumn = (key: string, checked: boolean) => {
        const newHidden = checked
            ? hiddenColumns.filter(k => k !== key)
            : [...hiddenColumns, key];
        setHiddenColumns(newHidden);
        saveColumnSettings(columnOrder, newHidden);
    };

    const handleResetColumns = () => {
        setColumnOrder(DEFAULT_ORDER);
        setHiddenColumns([...DEFAULT_HIDDEN]);
        saveColumnSettings(DEFAULT_ORDER, [...DEFAULT_HIDDEN]);
    };

    const [ filters, setFilters ] = useState<ExportTasksFilterRequest>({
        page: 1,
        pageSize: 10,
        sortDirection: 'desc',
        sortField: 'created_at'
    });

    const { isLoading, dictionaries, tasks, postCreateTask, deleteTask, fetchTasks, accessDenied } = useExportTasks(filters);

    // Подставляем текущую школу в фильтр, когда загрузятся и Redux, и справочники
    useEffect(() => {
        if (selectedSchool?.id && dictionaries?.schools && !filters.school_id) {
            const schoolId = Number(selectedSchool.id);
            setFilters((prev) => ({ ...prev, school_id: schoolId }));
        }
    }, [selectedSchool, dictionaries]);

    const onCloseHelpDrawer = () => {
        setHelpDrawerVisible(false);
    };

    const openHelpLink = () => {
        window.open(HELP_LINK_IMPORT, '_blank');
    };

    useEffect(() => {
        const drawerBody = document.querySelector('.ant-drawer-body');
        if (drawerBody) {
            (drawerBody as HTMLElement).style.padding = '0';
        }
        function postMessageToIframe(data: any) {
            const iframe = iframeRef.current;
            if (iframe && iframe.contentWindow) {
                iframe.contentWindow.postMessage(data, 'https://help.scananalytics.ru');
            }
        }
        if (helpDrawerVisible) {
            postMessageToIframe('deleteElement');
        }
    });

    const handleChangeSelect = (field: keyof ExportTasksFilterRequest, value: unknown) => {
        setFilters((prev) => ({ ...prev, [field]: value, page: 1 }));
    };

    // При смене школы — сбрасываем зависимые фильтры
    const handleChangeSchool = (schoolId: number) => {
        setFilters((prev) => ({
            ...prev,
            school_id: schoolId,
            created_by_user_ids: undefined,
            ad_cabinet_ids: undefined,
            page: 1
        }));
    };

    const clearFilters = () => {
        setFilters({
            page: 1,
            pageSize: filters.pageSize || 10,
            sortDirection: 'desc',
            sortField: 'created_at'
        });
    };

    const hasActiveFilters = !!(
        filters.source_ids?.length ||
        filters.task_type_ids?.length ||
        filters.creation_type_ids?.length ||
        filters.created_by_user_ids?.length ||
        filters.ad_cabinet_ids?.length ||
        filters.status_ids?.length ||
        filters.task_created_from ||
        filters.task_created_to ||
        filters.export_created_from ||
        filters.export_created_to
    );

    const handleCreateTask = async (values: ExportTaskCreateRequest): Promise<boolean> => {
        try {
            const { data } = await postCreateTask(values);
            showSuccessMessage(data.message);
            setModalVisible(false);
            fetchTasks();
            return true;
        } catch (e: any) {
            const errorMessage = e?.response?.data?.message || e?.message || 'Ошибка при создании задачи';
            showErrorMessage(errorMessage);
            return false;
        }
    };

    const handlePageChange = (page: number, pageSize: number) => {
        setFilters(prev => ({ ...prev, page, pageSize }));
    };

    const handleTableChange = (
        pagination: any,
        _tableFilters: any,
        sorter: any
    ) => {
        setFilters(prev => ({
            ...prev,
            page: pagination.current || 1,
            pageSize: pagination.pageSize || 20,
            sortField: sorter.columnKey || sorter.field || 'created_at',
            sortDirection: sorter.order === 'ascend' ? 'asc' : 'desc' as 'asc' | 'desc'
        }));
    };

    const handleDeleteTask = useCallback(async (taskId: number) => {
        try {
            const { data } = await deleteTask(taskId);
            showSuccessMessage(data.message || 'Задача успешно удалена');
            fetchTasks();
        } catch (e: any) {
            const errorMessage = e?.response?.data?.message || e?.message || 'Ошибка при удалении задачи';
            showErrorMessage(errorMessage);
        }
    }, [deleteTask, fetchTasks]);

    // Удалять можно только задачи со статусом "Создана задача"
    const canDeleteTask = (statusName: string) => {
        return statusName === 'Создана задача';
    };

    const columns: ColumnsType<ExportTask> = useMemo(() => [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'task_id',
            width: 60,
            align: 'center',
            render: (id, record) => <b>{id || record.task_id || '-'}</b>,
            sorter: true,
        },
        {
            title: 'Источник',
            dataIndex: 'source_name',
            key: 'source_name',
            width: 110,
            ellipsis: true,
            render: (text) => <Tooltip title={text}>{text}</Tooltip>,
            sorter: true,
        },
        {
            title: 'Школа / Аккаунт РК',
            dataIndex: 'source_title',
            key: 'source_title',
            width: 170,
            ellipsis: true,
            render: (text) => <Tooltip title={text}>{text}</Tooltip>,
        },
        {
            title: 'Тип задачи',
            dataIndex: 'task_type_name',
            key: 'task_type_name',
            width: 150,
            sorter: true,
            render: (text) => (
                <Tag style={{ whiteSpace: 'normal', lineHeight: 1.3 }} color="geekblue">
                    {text?.replace('Выгрузка', 'Импорт')}
                </Tag>
            ),
        },
        {
            title: 'Тип создания',
            dataIndex: 'creation_type_name',
            key: 'creation_type_name',
            width: 140,
            sorter: true,
            render: (text) => (
                <Tag style={{ whiteSpace: 'normal', lineHeight: 1.3 }} color="purple">
                    {text}
                </Tag>
            ),
        },
        {
            title: 'Период данных',
            key: 'period',
            width: 190,
            render: (_, record) => {
                if (!record.period_start_date && !record.period_end_date) {
                    return <span>—</span>;
                }
                const start = record.period_start_date
                    ? new Date(record.period_start_date).toLocaleDateString('ru-RU')
                    : '-';
                const end = record.period_end_date
                    ? new Date(record.period_end_date).toLocaleDateString('ru-RU')
                    : '-';
                return <span>{start} — {end}</span>;
            },
        },
        {
            title: 'ID выгрузки GC',
            dataIndex: 'external_export_id',
            key: 'external_export_id',
            width: 140,
            render: (value, record) => {
                if (record.source_name === 'GetCourse' && value) {
                    return (
                        <a
                            href={`https://${record.source_title}/pl/export/download?exportTaskId=${value}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {value}
                        </a>
                    );
                }
                return '—';
            },
        },
        {
            title: 'Статус',
            dataIndex: 'status_name',
            key: 'status_name',
            width: 150,
            align: 'center',
            sorter: true,
            render: (status) => {
                const color = STATUS_COLORS[status] || 'default';
                return (
                    <Tag
                        color={color}
                        style={{ whiteSpace: 'normal', lineHeight: 1.3, textAlign: 'center' }}
                    >
                        {status?.replace('_', ' ')}
                    </Tag>
                );
            },
        },
        {
            title: 'Прогресс',
            dataIndex: 'progress_percentage',
            key: 'progress_percentage',
            width: 160,
            render: (value) => {
                const { percent, text } = splitProgressText(value);
                return (
                    <div style={{ maxWidth: 150 }}>
                        {percent !== null && <Progress percent={percent} size="small" style={{ marginBottom: 4 }} />}
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                            {text}
                        </Typography.Text>
                    </div>
                );
            },
        },
        {
            title: 'Дата создания',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 155,
            render: (date) => date ? new Date(date).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(',', '') : '-',
            sorter: true,
        },
        {
            title: 'Дата создания в GC',
            dataIndex: 'source_export_created_at',
            key: 'source_export_created_at',
            width: 175,
            render: (date) => date ? new Date(date).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(',', '') : '-',
            sorter: true,
        },
        {
            title: 'Дата завершения',
            dataIndex: 'completed_at',
            key: 'completed_at',
            width: 165,
            render: (date) => date ? new Date(date).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(',', '') : '-',
            sorter: true,
        },
        {
            title: 'Создатель',
            dataIndex: 'created_by_user_name',
            key: 'created_by_user_name',
            width: 120,
            ellipsis: true,
            sorter: true,
        },
        {
            title: 'Ошибка',
            dataIndex: 'error_message',
            key: 'error_message',
            width: 80,
            align: 'center',
            render: (text) =>
                text ? (
                    <Tooltip placement="topLeft" title={text}>
                        <span style={{ color: 'red', cursor: 'help', fontSize: 16 }}>⚠️</span>
                    </Tooltip>
                ) : (
                    <span style={{ color: '#bfbfbf' }}>—</span>
                ),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 80,
            align: 'center',
            render: (_, record) => (
                canDeleteTask(record.status_name) ? (
                    <Popconfirm
                        title="Удалить задачу? Это действие нельзя отменить."
                        onConfirm={() => handleDeleteTask(record.id || record.task_id!)}
                        okText="Удалить"
                        cancelText="Отмена"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        />
                    </Popconfirm>
                ) : (
                    <Tooltip title="Удаление доступно только для задач со статусом «Создана задача»">
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            size="small"
                            disabled
                        />
                    </Tooltip>
                )
            ),
        },
    ], [handleDeleteTask]);

    // Применяем порядок и видимость
    const visibleColumns = useMemo(() => {
        const columnsMap = new Map<string, typeof columns[0]>();
        columns.forEach(col => {
            const key = (col as any).key as string;
            if (key) columnsMap.set(key, col);
        });

        const result: typeof columns = [];
        columnOrder.forEach(key => {
            if (!hiddenColumns.includes(key) && columnsMap.has(key)) {
                result.push(columnsMap.get(key)!);
            }
        });

        // Добавляем колонки, которых нет в order (на случай если добавились новые)
        columns.forEach(col => {
            const key = (col as any).key as string;
            if (key && !columnOrder.includes(key) && !hiddenColumns.includes(key)) {
                result.push(col);
            }
        });

        return result;
    }, [columns, columnOrder, hiddenColumns]);

    const scrollX = useMemo(() => {
        return visibleColumns.reduce((sum, col) => sum + ((col as any).width || 100), 0);
    }, [visibleColumns]);

    const columnSettingsContent = (
        <div style={{ width: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 500, fontSize: 14 }}>Настройки колонок</span>
                <CloseOutlined
                    style={{ fontSize: 14, cursor: 'pointer', color: '#999' }}
                    onClick={() => setColumnSettingsVisible(false)}
                />
            </div>
            <ReactSortable
                list={sortableItems}
                setList={handleSortableChange}
                handle=".drag-handle"
                animation={150}
                tag="div"
            >
                {sortableItems.map(item => (
                    <div
                        key={item.key}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '4px 0',
                            borderBottom: '1px solid #f0f0f0',
                        }}
                    >
                        <HolderOutlined
                            className="drag-handle"
                            style={{ cursor: 'grab', color: '#bfbfbf', fontSize: 14 }}
                        />
                        <Checkbox
                            checked={!hiddenColumns.includes(item.key)}
                            disabled={item.key === 'actions'}
                            onChange={(e) => handleToggleColumn(item.key, e.target.checked)}
                        />
                        <span style={{ fontSize: 13 }}>{item.title}</span>
                    </div>
                ))}
            </ReactSortable>
            <div style={{ marginTop: 8, textAlign: 'right' }}>
                <Button type="link" size="small" onClick={handleResetColumns}>
                    Сбросить
                </Button>
            </div>
        </div>
    );

    return (
        <MainLayout pageTitle="Импорт данных">
            <div className="page-wrapper">
                <div style={{ padding: '15px 24px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                        <Title level={1} style={{ margin: 0, color: '#000', fontSize: '23px', fontWeight: '600' }}>
                            Импорт данных
                        </Title>
                        <div
                            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                            onClick={() => setHelpDrawerVisible(true)}
                        >
                            <div style={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: '#ff4d4f',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: 'bold'
                            }}>
                                ?
                            </div>
                            <Text style={{ color: '#ff4d4f', fontSize: '14px' }}>
                                Как работать с импортом
                            </Text>
                        </div>
                    </div>
                </div>
                <div className="page-content">
                    {accessDenied ? (
                        <Result
                            icon={<LockOutlined style={{ color: '#ff4d4f' }} />}
                            title="Нет доступа"
                            subTitle="У вас недостаточно прав для просмотра задач импорта. Обратитесь к владельцу школы для получения доступа."
                        />
                    ) : (
                    <>
                    <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
                        </Text>
                        <Space size={16}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setModalVisible(true)}
                                style={{
                                    backgroundColor: '#ff4d4f',
                                    borderColor: '#ff4d4f',
                                    borderRadius: '5px',
                                    color: '#fff'
                                }}
                            >
                            Создать импорт
                            </Button>
                            <Popover
                                content={columnSettingsContent}
                                trigger="click"
                                placement="bottomRight"
                                visible={columnSettingsVisible}
                                onVisibleChange={setColumnSettingsVisible}
                            >
                                <div
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                                    onClick={() => setColumnSettingsVisible(!columnSettingsVisible)}
                                >
                                    <SettingOutlined style={{ fontSize: 16, color: '#595959' }} />
                                    <Text style={{ color: '#595959', fontSize: '14px', textDecoration: 'underline' }}>
                                        Настройка колонок
                                    </Text>
                                </div>
                            </Popover>
                        </Space>
                    </div>

                    {showFilters && (
                        <Card size="small" style={{ marginBottom: 24, border: '1px solid #d9d9d9', borderRadius: '8px' }}>
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Дата создания задачи на импорт</Text>
                                            <DatePicker.RangePicker
                                                format="DD.MM.YYYY"
                                                placeholder={[ 'Дата от', 'Дата до' ]}
                                                style={{ width: '100%' }}
                                                value={
                                                    filters.task_created_from && filters.task_created_to
                                                        ? [ moment(filters.task_created_from), moment(filters.task_created_to) ]
                                                        : undefined
                                                }
                                                onChange={(dates) => {
                                                    handleChangeSelect(
                                                        FILTER_FIELDS.taskCreatedFrom,
                                                        dates?.[0]?.format('YYYY-MM-DD')
                                                    );
                                                    handleChangeSelect(
                                                        FILTER_FIELDS.taskCreatedTo,
                                                        dates?.[1]?.format('YYYY-MM-DD')
                                                    );
                                                }}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Источники</Text>
                                            <Select
                                                mode="multiple"
                                                maxTagCount="responsive"
                                                placeholder="Выберите источники"
                                                value={filters.source_ids}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                onChange={(value) =>
                                                    handleChangeSelect(FILTER_FIELDS.sourceIds, value)
                                                }
                                                options={dictionaries?.sources?.map((source) => ({
                                                    label: source.name,
                                                    value: source.id
                                                }))}
                                                style={{ width: '100%' }}
                                                allowClear
                                                dropdownMatchSelectWidth={false}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Что загружаем</Text>
                                            <Select
                                                mode="multiple"
                                                maxTagCount="responsive"
                                                placeholder="Выберите типы выгрузок"
                                                value={filters.task_type_ids}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                onChange={(value) =>
                                                    handleChangeSelect(FILTER_FIELDS.taskTypeIds, value)
                                                }
                                                options={dictionaries?.task_types?.map((type) => ({
                                                    label: type.name?.replace('Выгрузка', 'Импорт'),
                                                    value: type.id
                                                }))}
                                                style={{ width: '100%' }}
                                                allowClear
                                                dropdownMatchSelectWidth={false}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Типы создания</Text>
                                            <Select
                                                mode="multiple"
                                                maxTagCount="responsive"
                                                placeholder="Выберите типы создания"
                                                value={filters.creation_type_ids}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                onChange={(value) =>
                                                    handleChangeSelect(FILTER_FIELDS.creationTypeIds, value)
                                                }
                                                options={dictionaries?.task_creation_types?.map((type) => ({
                                                    label: type.name,
                                                    value: type.id
                                                }))}
                                                style={{ width: '100%' }}
                                                allowClear
                                                dropdownMatchSelectWidth={false}
                                            />
                                        </Space>
                                    </Col>
                                </Row>
                                <Row gutter={[16, 16]}>
                                    <Col span={7}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Рекламные кабинеты</Text>
                                            <Select
                                                mode="multiple"
                                                maxTagCount="responsive"
                                                placeholder="Выберите кабинеты"
                                                value={filters.ad_cabinet_ids}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                onChange={(value) =>
                                                    handleChangeSelect(FILTER_FIELDS.adCabinetIds, value)
                                                }
                                                options={dictionaries?.ad_cabinets?.map((cabinet) => {
                                                    const source = dictionaries?.sources?.find(
                                                        (s) => s.id === cabinet.source_id
                                                    );
                                                    return {
                                                        label: source
                                                            ? `${source.name} — ${cabinet.name} (#${cabinet.id})`
                                                            : `${cabinet.name} (#${cabinet.id})`,
                                                        value: cabinet.id
                                                    };
                                                })}
                                                style={{ width: '100%' }}
                                                allowClear
                                                dropdownMatchSelectWidth={false}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={5}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Статус</Text>
                                            <Select
                                                mode="multiple"
                                                maxTagCount="responsive"
                                                placeholder="Выберите статусы"
                                                value={filters.status_ids}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                onChange={(value) =>
                                                    handleChangeSelect(FILTER_FIELDS.statusIds, value)
                                                }
                                                options={dictionaries?.task_statuses?.map((status) => ({
                                                    label: status.name,
                                                    value: status.id
                                                }))}
                                                style={{ width: '100%' }}
                                                allowClear
                                                dropdownMatchSelectWidth={false}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Кто создал задачу</Text>
                                            <Select
                                                mode="multiple"
                                                maxTagCount="responsive"
                                                placeholder="Выберите создателя"
                                                value={filters.created_by_user_ids}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                onChange={(value) =>
                                                    handleChangeSelect(FILTER_FIELDS.createdByUserIds, value)
                                                }
                                                options={dictionaries?.users
                                                    ?.filter((user, index, self) =>
                                                        index === self.findIndex((u) => u.id === user.id)
                                                    )
                                                    .map((user) => ({
                                                        label: user.name,
                                                        value: user.id
                                                    }))}
                                                style={{ width: '100%' }}
                                                allowClear
                                                dropdownMatchSelectWidth={false}
                                            />
                                        </Space>
                                    </Col>
                                    {permission([ ROLES.super ]) && (
                                        <Col span={6}>
                                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                <Text>Школа</Text>
                                                <Select
                                                    placeholder="Выберите школу"
                                                    value={filters.school_id}
                                                    showSearch
                                                    filterOption={(input, option) =>
                                                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                    }
                                                    onChange={(value) => handleChangeSchool(value)}
                                                    options={dictionaries?.schools?.map((school) => ({
                                                        label: school.name,
                                                        value: school.id
                                                    }))}
                                                    style={{ width: '100%' }}
                                                    dropdownMatchSelectWidth={false}
                                                />
                                            </Space>
                                        </Col>
                                    )}
                                </Row>
                                {hasActiveFilters && (
                                    <div style={{ textAlign: 'right' }}>
                                        <Text
                                            type="secondary"
                                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            onClick={clearFilters}
                                        >
                                            Очистить фильтры
                                        </Text>
                                    </div>
                                )}
                            </Space>
                        </Card>
                    )}

                    {/* Таблица */}
                    <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
                        <Table
                            loading={isLoading}
                            columns={visibleColumns}
                            dataSource={tasks?.data}
                            rowKey={(record) => record.id || record.task_id || Math.random()}
                            size="small"
                            bordered
                            scroll={{ x: scrollX }}
                            onChange={handleTableChange}
                            pagination={{
                                current: filters.page,
                                pageSize: filters.pageSize,
                                total: tasks?.pagination?.total || 0,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                pageSizeOptions: [ '10', '20', '50', '100' ],
                                showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
                                style: { paddingRight: 16 },
                            }}
                        />
                    </div>
                    </>
                    )}
                </div>
                <Drawer
                    placement="right"
                    onClose={onCloseHelpDrawer}
                    open={helpDrawerVisible}
                    width={650}
                    closable={false}
                    rootStyle={{ padding: 0, overflow: 'hidden' }}
                >
                    <div style={{ position: 'absolute', top: 30, left: -40, zIndex: 1000 }}>
                        <Button
                            icon={<CloseOutlined />}
                            onClick={onCloseHelpDrawer}
                            shape="circle"
                            style={{ display: 'block', marginBottom: 10 }}
                        />
                        <Button
                            icon={<LinkOutlined />}
                            onClick={openHelpLink}
                            shape="circle"
                            style={{ display: 'block' }}
                        />
                    </div>
                    <div style={{ marginTop: '10px' }}></div>
                    <iframe
                        className="help-iframe"
                        ref={iframeRef}
                        src={`${HELP_LINK_IMPORT}?timestamp=${new Date().getTime()}`}
                    />
                </Drawer>

                <CreateTaskModal
                    open={isModalVisible}
                    onCancel={() => setModalVisible(false)}
                    onCreate={handleCreateTask}
                    taskTypes={dictionaries?.task_types ?? []}
                    sources={dictionaries?.sources ?? []}
                    adCabinets={dictionaries?.ad_cabinets ?? []}
                    schoolId={permission([ ROLES.super ]) ? filters.school_id : Number(selectedSchool?.id)}
                />
            </div>
        </MainLayout>
    );
};

export default ImportPage;
